import React from 'react';
import { Button, Input, Radio, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { emptyQuizQuestion } from '../../utils/trainingModel';

const { Text } = Typography;

/**
 * Controlled quiz-spec editor. `value` is `{ questions: [{ prompt, options[], correctIndex }] }`;
 * the answer key stays in the authoring surface only — the API strips `correctIndex` from any
 * learner GET and scores submissions server-side.
 */
const QuizEditor = ({ value, onChange }) => {
    const questions = value?.questions || [];
    const update = (next) => onChange({ questions: next });
    const patchQuestion = (i, patch) => update(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {questions.map((q, i) => (
                <div key={i} style={{ border: '1px solid var(--color-border)', borderRadius: 10, padding: 12 }} data-testid={`quiz-question-${i}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <Text strong>Question {i + 1}</Text>
                        <Button
                            size="small"
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            aria-label={`Remove question ${i + 1}`}
                            disabled={questions.length <= 1}
                            onClick={() => update(questions.filter((_, idx) => idx !== i))}
                        />
                    </div>
                    <Input.TextArea
                        aria-label={`Question ${i + 1} prompt`}
                        rows={2}
                        value={q.prompt}
                        onChange={(e) => patchQuestion(i, { prompt: e.target.value })}
                        placeholder="Question text"
                    />
                    <Radio.Group
                        style={{ width: '100%', marginTop: 8 }}
                        value={q.correctIndex}
                        onChange={(e) => patchQuestion(i, { correctIndex: e.target.value })}
                    >
                        {q.options.map((opt, oi) => (
                            <div key={oi} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                                <Radio value={oi} aria-label={`Mark option ${oi + 1} of question ${i + 1} correct`} />
                                <Input
                                    aria-label={`Question ${i + 1} option ${oi + 1}`}
                                    value={opt}
                                    onChange={(e) =>
                                        patchQuestion(i, { options: q.options.map((o, idx) => (idx === oi ? e.target.value : o)) })
                                    }
                                    placeholder={`Option ${oi + 1}`}
                                />
                                <Button
                                    size="small"
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    aria-label={`Remove option ${oi + 1} of question ${i + 1}`}
                                    disabled={q.options.length <= 2}
                                    onClick={() => {
                                        const options = q.options.filter((_, idx) => idx !== oi);
                                        const correctIndex = q.correctIndex === oi ? 0 : q.correctIndex > oi ? q.correctIndex - 1 : q.correctIndex;
                                        patchQuestion(i, { options, correctIndex });
                                    }}
                                />
                            </div>
                        ))}
                    </Radio.Group>
                    <Button
                        size="small"
                        icon={<PlusOutlined />}
                        disabled={q.options.length >= 6}
                        onClick={() => patchQuestion(i, { options: [...q.options, ''] })}
                    >
                        Add option
                    </Button>
                    <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
                        Select the radio beside the correct answer.
                    </Text>
                </div>
            ))}
            <Button icon={<PlusOutlined />} onClick={() => update([...questions, emptyQuizQuestion()])}>
                Add question
            </Button>
        </div>
    );
};

export default QuizEditor;
